import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { PeriodParams } from './period.model';

/**
 * Creates period visualization for periodic functions (sine, cosine, tangent).
 *
 * Features:
 * - Horizontal double-arrow line showing one period length
 * - Vertical dashed trace lines from reference points up to the arrow
 * - Arrow is offset above the curve by ~1 unit
 *
 * Subparts:
 * - arrow: The horizontal double-arrow showing period length
 * - leftTrace: Vertical dashed line from first reference point up to arrow
 * - rightTrace: Vertical dashed line from second reference point up to arrow
 *
 * Period formulas:
 * - sine/cosine: period = 2π/b
 * - tangent: period = π/b
 */
export class PeriodCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'period';

  /** Vertical offset above the curve for the period arrow */
  private readonly arrowOffset = 1;

  private readonly supportedTypes = ['sine', 'cosine', 'tangent-fn'];

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as PeriodParams | undefined;

    if (!params?.of) {
      throw new Error('Period feature requires params.of to specify the parent function');
    }

    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    if (!this.supportedTypes.includes(parentElement.type)) {
      throw new Error(
        `Period feature only supports ${this.supportedTypes.join(', ')} functions, got ${parentElement.type}`,
      );
    }

    const objects: GeometryElement[] = [];

    // Resolve styles for each subpart
    const arrowStyle = resolveFeatureStyle('line', this.featureType, context, display, ['arrow']);
    const leftTraceStyle = resolveFeatureStyle('line', this.featureType, context, display, ['leftTrace']);
    const rightTraceStyle = resolveFeatureStyle('line', this.featureType, context, display, ['rightTrace']);

    const arrowVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['arrow']);
    const leftTraceVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['leftTrace']);
    const rightTraceVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['rightTrace']);

    // Helper to get function params reactively
    const getParams = () => parentElement.getParams?.() ?? {};

    // Get the period based on function type
    const getPeriod = () => {
      const p = getParams();
      const b = p['b'] ?? 1;
      if (parentElement.type === 'tangent-fn') {
        return Math.PI / b; // Tangent period is π/b
      }
      return (2 * Math.PI) / b; // Sine/cosine period is 2π/b
    };

    // Get the x position for first reference point
    // For sine/cosine: first maximum
    // For tangent: first root (at x = c)
    const getFirstRefX = () => {
      const p = getParams();
      const b = p['b'] ?? 1;
      const c = p['c'] ?? 0;
      if (parentElement.type === 'sine') {
        return c + Math.PI / (2 * b);
      } else if (parentElement.type === 'cosine') {
        const a = p['a'] ?? 1;
        return a >= 0 ? c : c + Math.PI / b;
      } else {
        // tangent-fn: use the root at x = c
        return c;
      }
    };

    // Get the x position for second reference point (one period later)
    const getSecondRefX = () => {
      return getFirstRefX() + getPeriod();
    };

    // Get the y position at reference point
    // For sine/cosine: maximum y
    // For tangent: use a fixed height since tangent is unbounded
    const getRefY = () => {
      const p = getParams();
      if (parentElement.type === 'tangent-fn') {
        // For tangent, use d (midline) as reference since it has no max
        return p['d'] ?? 0;
      }
      return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
    };

    // Get the y position for the arrow (above the reference with offset)
    const getArrowY = () => {
      const p = getParams();
      if (parentElement.type === 'tangent-fn') {
        // For tangent, position arrow at a reasonable height above d
        return (p['d'] ?? 0) + 3;
      }
      return getRefY() + this.arrowOffset;
    };

    // Left trace: vertical dashed line from first ref up to arrow level
    const leftTraceResult = elementFactory.line({
      a: [getFirstRefX, getRefY],
      b: [getFirstRefX, getArrowY],
      name: 'leftTrace',
      style: {
        ...leftTraceStyle,
        visible: leftTraceVisible,
      },
    });
    objects.push(leftTraceResult.line);
    leftTraceResult.a.setAttribute({ visible: false });
    leftTraceResult.b.setAttribute({ visible: false });
    if (leftTraceResult.lineLabel) {
      objects.push(leftTraceResult.lineLabel);
    }

    // Right trace: vertical dashed line from second ref up to arrow level
    const rightTraceResult = elementFactory.line({
      a: [getSecondRefX, getRefY],
      b: [getSecondRefX, getArrowY],
      name: 'rightTrace',
      style: {
        ...rightTraceStyle,
        visible: rightTraceVisible,
      },
    });
    objects.push(rightTraceResult.line);
    rightTraceResult.a.setAttribute({ visible: false });
    rightTraceResult.b.setAttribute({ visible: false });
    if (rightTraceResult.lineLabel) {
      objects.push(rightTraceResult.lineLabel);
    }

    // Period arrow: horizontal double-arrow at arrow level
    const periodArrowResult = elementFactory.line({
      a: [getFirstRefX, getArrowY],
      b: [getSecondRefX, getArrowY],
      name: 'T',
      style: {
        ...arrowStyle,
        visible: arrowVisible,
        arrows: { first: true, last: true },
      },
    });
    objects.push(periodArrowResult.line);
    periodArrowResult.a.setAttribute({ visible: false });
    periodArrowResult.b.setAttribute({ visible: false });
    if (periodArrowResult.lineLabel) {
      objects.push(periodArrowResult.lineLabel);
    }

    return {
      id,
      type: 'period',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
